/* waveDivider.ts
 *
 * <wave-divider> — mathematically generated and randomized wobbly wave transition.
 * Sits at the top of the `#services` section, filled with black, cutting into
 * the cream hero section above to make a smooth wave boundary.
 *
 * Uses spring physics for elastic shape morphing and scroll velocity interaction.
 */

interface Point {
    x: number;
    y: number;
}

export class WaveDivider extends HTMLElement {
    private pointsCount = 8;
    private current: number[] = [];
    private targets: number[] = [];
    private vels: number[] = [];

    // spring physics parameters for wobbly bounce / elastic effect
    private stiffness = 0.05;
    private damping = 0.83;

    private isAnimating = false;
    private lastBand = -1;

    private pathElement: SVGPathElement | null = null;

    connectedCallback() {
        this.setAttribute('aria-hidden', 'true');
        
        // Initialize HTML structure with SVG
        this.innerHTML = `
            <svg viewBox="0 0 1000 200" preserveAspectRatio="none" focusable="false">
                <path d="" />
            </svg>
        `;
        this.pathElement = this.querySelector('path');

        // Initialize points to flat baseline (bottom edge of viewBox)
        for (let i = 0; i < this.pointsCount; i++) {
            this.current.push(200);
            this.targets.push(200);
            this.vels.push(0);
        }

        // Render initial flat state
        this.updatePath();

        // Animate/spring up to the first random wave shape
        setTimeout(() => {
            this.triggerMorph();
        }, 50);
    }

    /**
     * Generates a new set of random target Y coordinates for the wave points.
     */
    public triggerMorph() {
        // Base line in the viewBox (200px tall), shifted up to make the wave taller
        const baseLine = 90;
        // Maximum offset from the base line
        const variance = 50;

        for (let i = 0; i < this.pointsCount; i++) {
            // Keep the outer ends (first and last points) a bit closer to the baseLine
            // to prevent extreme slopes at the screen edges
            const isEdge = i === 0 || i === this.pointsCount - 1;
            const factor = isEdge ? 0.5 : 1.0;
            
            // Random target height
            this.targets[i] = baseLine + (Math.random() - 0.5) * variance * factor;
        }

        this.startLoop();
    }

    /**
     * Updates progress and applies scroll velocity to the spring simulation.
     * Called by the scroll handler in main.ts.
     */
    public updateProgress(p: number, velocity: number) {
        // 1. Add scroll velocity force to spring velocities (makes the wave wobble on scroll)
        if (Math.abs(velocity) > 0.2) {
            for (let i = 0; i < this.pointsCount; i++) {
                // Alternate direction of push per point to create organic ripples
                const direction = i % 2 === 0 ? 1 : -1;
                // Add velocity scaled down to spring coordinates
                this.vels[i] += velocity * direction * 0.04;
            }
            this.startLoop();
        }

        // 2. Trigger random morph when scroll crosses 12.5% progress increments
        const band = Math.floor(p * 8);
        if (band !== this.lastBand && p > 0.05 && p < 0.95) {
            this.lastBand = band;
            this.triggerMorph();
        }
    }

    private startLoop() {
        if (this.isAnimating) return;
        this.isAnimating = true;
        requestAnimationFrame(this.tick.bind(this));
    }

    private tick() {
        let allSettled = true;

        for (let i = 0; i < this.pointsCount; i++) {
            // Spring force: F = -k * x
            const force = (this.targets[i] - this.current[i]) * this.stiffness;
            this.vels[i] += force;
            this.vels[i] *= this.damping;
            this.current[i] += this.vels[i];

            // Settle threshold check
            const distance = Math.abs(this.targets[i] - this.current[i]);
            const speed = Math.abs(this.vels[i]);
            if (speed > 0.005 || distance > 0.05) {
                allSettled = false;
            }
        }

        this.updatePath();

        if (!allSettled) {
            requestAnimationFrame(this.tick.bind(this));
        } else {
            this.isAnimating = false;
        }
    }

    private updatePath() {
        const points: Point[] = [];
        for (let i = 0; i < this.pointsCount; i++) {
            points.push({
                x: (i / (this.pointsCount - 1)) * 1000,
                y: this.current[i]
            });
        }

        const pathData = this.getSplinePath(points);
        if (this.pathElement) {
            this.pathElement.setAttribute('d', pathData);
        }
    }

    /**
     * Catmull-Rom spline path generator for smooth organic waves
     */
    private getSplinePath(points: Point[]): string {
        const N = points.length;
        if (N < 2) return '';

        // Start path at bottom-left, then draw up to the first point
        let path = `M 0 200 L 0 ${points[0].y.toFixed(1)}`;

        const k = 0.25; // Spline tangent smoothing factor

        // Precompute tangents
        const tangents: Point[] = [];
        for (let i = 0; i < N; i++) {
            if (i === 0) {
                tangents.push({
                    x: (points[1].x - points[0].x) * k,
                    y: (points[1].y - points[0].y) * k
                });
            } else if (i === N - 1) {
                tangents.push({
                    x: (points[N - 1].x - points[N - 2].x) * k,
                    y: (points[N - 1].y - points[N - 2].y) * k
                });
            } else {
                tangents.push({
                    x: (points[i + 1].x - points[i - 1].x) * k,
                    y: (points[i + 1].y - points[i - 1].y) * k
                });
            }
        }

        // Draw cubic Beziers
        for (let i = 0; i < N - 1; i++) {
            const p0 = points[i];
            const p1 = points[i + 1];
            const t0 = tangents[i];
            const t1 = tangents[i + 1];

            const cp1x = p0.x + t0.x;
            const cp1y = p0.y + t0.y;
            const cp2x = p1.x - t1.x;
            const cp2y = p1.y - t1.y;

            path += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
        }

        // Close the path
        path += ` L 1000 200 Z`;
        return path;
    }
}

customElements.define('wave-divider', WaveDivider);
